<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("forecast.title") }}
          </h6>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row align-items-center">
            <div class="col-md-3">
              <base-input
                v-model="searchQuery"
                :placeholder="$t('search')"
                prepend-icon="fas fa-search"
              ></base-input>
            </div>
            <div class="col-md-4">
              <el-radio-group v-model="selectedWindow" size="small">
                <el-radio-button :label="7">7 {{ $t("forecast.days") }}</el-radio-button>
                <el-radio-button :label="15">15 {{ $t("forecast.days") }}</el-radio-button>
                <el-radio-button :label="30">30 {{ $t("forecast.days") }}</el-radio-button>
              </el-radio-group>
            </div>
            <div class="col-md-5 d-flex align-items-center flex-wrap" style="gap:8px;">
              <base-button type="primary" size="sm" @click="loadData()">
                <i class="fa fa-sync"></i> {{ $t("forecast.refresh") }}
              </base-button>
              <base-button type="success" size="sm" @click="exportExcel()">
                <i class="fas fa-file-excel"></i> {{ $t("forecast.export_excel") }}
              </base-button>
              <div class="d-flex align-items-center">
                <el-switch v-model="showZero" active-color="#409EFF"></el-switch>
                <span class="ml-2 text-sm">{{ $t("forecast.show_zero") }}</span>
              </div>
            </div>
          </div>
        </template>

        <el-table
          :data="filteredData"
          row-key="id"
          header-row-class-name="thead-light"
          v-loading="loading"
          :default-sort="{prop: 'days_can_sale', order: 'ascending'}"
        >
          <el-table-column min-width="80" align="center">
            <template slot-scope="{ row }">
              <img
                v-if="row.image"
                :src="getImageUrl(row.image)"
                height="50px"
                style="max-width: 60px; object-fit: contain;"
              />
            </template>
          </el-table-column>

          <el-table-column :label="$t('forecast.product_name')" prop="name" min-width="180" sortable>
          </el-table-column>

          <el-table-column :label="$t('forecast.barcode')" prop="barcode" min-width="120">
          </el-table-column>

          <el-table-column :label="$t('forecast.current_stock')" prop="balance" min-width="100" align="right" sortable>
            <template slot-scope="{ row }">
              {{ row.balance.toLocaleString() }}
            </template>
          </el-table-column>

          <el-table-column :label="$t('forecast.usage')" min-width="80" align="right" sortable :sort-method="sortByUsage">
            <template slot-scope="{ row }">
              {{ getUsage(row).toLocaleString() }}
            </template>
          </el-table-column>

          <el-table-column :label="$t('forecast.daily_avg')" min-width="90" align="right" sortable :sort-method="sortByDailyAvg">
            <template slot-scope="{ row }">
              {{ getDailyAvg(row).toFixed(2) }}
            </template>
          </el-table-column>

          <el-table-column :label="$t('forecast.days_can_sale')" min-width="110" align="right" sortable :sort-method="sortByDaysCanSale">
            <template slot-scope="{ row }">
              <span :class="getDaysClass(row)">
                {{ getDaysCanSale(row) > 0 ? getDaysCanSale(row).toFixed(1) : '-' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('forecast.status')" min-width="160" align="center">
            <template slot-scope="{ row }">
              <badge :type="getStatusBadgeType(row.forecast_status)" style="font-size: 0.9rem; padding: 0.5em 0.9em;">
                {{ $t('forecast.status_' + row.forecast_status) }}
              </badge>
            </template>
          </el-table-column>
        </el-table>

        <div slot="footer" class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap">
          <p class="card-category">
            {{ $t("data") }} {{ filteredData.length }} {{ $t("of") }} {{ forecastData.length }}
          </p>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { Table, TableColumn, RadioGroup, RadioButton, Switch } from "element-ui";
import Badge from "@/components/Badge";
import * as XLSX from "xlsx";

export default {
  components: {
    Badge,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [RadioGroup.name]: RadioGroup,
    [RadioButton.name]: RadioButton,
    [Switch.name]: Switch,
  },

  computed: {
    ...mapGetters({
      forecastData: "WmsProduct/getDataForecast",
    }),

    filteredData() {
      let data = this.forecastData;
      if (!this.showZero) {
        data = data.filter(item => this.getUsage(item) > 0 || this.getDailyAvg(item) > 0);
      }
      if (!this.searchQuery) return data;
      const q = this.searchQuery.toLowerCase();
      return data.filter(
        (item) =>
          (item.name && item.name.toLowerCase().includes(q)) ||
          (item.barcode && item.barcode.toLowerCase().includes(q))
      );
    },
  },

  data() {
    return {
      loading: false,
      searchQuery: "",
      selectedWindow: 7,
      showZero: false,
    };
  },

  mounted() {
    this.loadData();
  },

  methods: {
    ...mapActions({
      listForecast: "WmsProduct/listForecast",
    }),

    async loadData() {
      this.loading = true;
      try {
        await this.listForecast();
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },

    getImageUrl(image) {
      if (!image) return "";
      if (image.startsWith("http")) {
        // Only allow images from our own API domain
        const apiBase = process.env.VUE_APP_API_BASE_URL || "";
        if (apiBase && image.startsWith(apiBase)) return image;
        // Block external URLs
        return "";
      }
      // Sanitize relative path - remove path traversal
      const safeName = image.replace(/[./\\]/g, '').length > 0 ? image.replace(/\.\./g, '') : '';
      if (!safeName) return "";
      return process.env.VUE_APP_API_BASE_URL + "/files/wms_products/" + safeName;
    },

    getUsage(row) {
      if (this.selectedWindow === 7) return row.usage_7_days || 0;
      if (this.selectedWindow === 15) return row.usage_15_days || 0;
      return row.usage_30_days || 0;
    },

    getDailyAvg(row) {
      if (this.selectedWindow === 7) return row.daily_avg_7 || 0;
      if (this.selectedWindow === 15) return row.daily_avg_15 || 0;
      return row.daily_avg_30 || 0;
    },

    getDaysCanSale(row) {
      if (this.selectedWindow === 7) return row.days_can_sale_7 || 0;
      if (this.selectedWindow === 15) return row.days_can_sale_15 || 0;
      return row.days_can_sale_30 || 0;
    },

    getDaysClass(row) {
      const days = this.getDaysCanSale(row);
      if (row.balance <= 0) return "text-dark font-weight-bold";
      if (days > 0 && days < 3) return "text-danger font-weight-bold";
      if (days > 0 && days < 7) return "text-warning font-weight-bold";
      return "text-success";
    },

    sortByUsage(a, b) {
      return this.getUsage(a) - this.getUsage(b);
    },
    sortByDailyAvg(a, b) {
      return this.getDailyAvg(a) - this.getDailyAvg(b);
    },
    sortByDaysCanSale(a, b) {
      return this.getDaysCanSale(a) - this.getDaysCanSale(b);
    },

    getStatusBadgeType(status) {
      switch (status) {
        case "out": return "dark";
        case "critical": return "danger";
        case "low": return "warning";
        case "ok": return "success";
        default: return "default";
      }
    },

    buildSheetData(days) {
      const headers = ["Product", "Barcode", "Balance", `Usage (${days}d)`, `Daily Avg (${days}d)`, `Days Can Sale (${days}d)`, "Status"];
      const rows = this.forecastData.map(row => {
        const usage    = days === 7 ? (row.usage_7_days || 0)    : days === 15 ? (row.usage_15_days || 0)    : (row.usage_30_days || 0);
        const avg      = days === 7 ? (row.daily_avg_7 || 0)     : days === 15 ? (row.daily_avg_15 || 0)     : (row.daily_avg_30 || 0);
        const canSale  = days === 7 ? (row.days_can_sale_7 || 0) : days === 15 ? (row.days_can_sale_15 || 0) : (row.days_can_sale_30 || 0);
        return [row.name, row.barcode, row.balance, usage, +avg.toFixed(2), +canSale.toFixed(1), this.$t('forecast.status_' + row.forecast_status)];
      });
      return [headers, ...rows];
    },

    exportExcel() {
      const wb = XLSX.utils.book_new();
      [7, 15, 30].forEach(days => {
        const ws = XLSX.utils.aoa_to_sheet(this.buildSheetData(days));
        XLSX.utils.book_append_sheet(wb, ws, `${days} Days`);
      });
      XLSX.writeFile(wb, `stock_forecast_${new Date().toISOString().slice(0, 10)}.xlsx`);
    },

  },
};
</script>
